import React, { useState, useEffect, useMemo } from "react";
import { Button, Paper, Typography, Box, Checkbox, FormControlLabel, TextField, Alert, MenuItem, Chip, Divider, CircularProgress, Snackbar } from "@mui/material";
import { useStore } from "../../app/stores/store";
import DataTable, { FilterProps, DataType } from "../../app/common/DataTable";
import { Member } from "../../app/models/member";
import { useTranslation } from "react-i18next";
import { useMemberStatusOptions } from "../../app/common/enums/memberStatus";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";

function parseSegments(text: string): { type: "text" | "placeholder"; value: string }[] {
    const result: { type: "text" | "placeholder"; value: string }[] = [];
    const re = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) result.push({ type: "text", value: text.slice(lastIndex, match.index) });
        result.push({ type: "placeholder", value: match[1] });
        lastIndex = re.lastIndex;
    }
    if (lastIndex < text.length) result.push({ type: "text", value: text.slice(lastIndex) });
    return result;
}

function PreviewText({ text, variables }: { text: string; variables: Record<string, string> }) {
    const segments = parseSegments(text);
    return (
        <span style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
            {segments.map((seg, i) => {
                if (seg.type === "text") return <span key={i}>{seg.value}</span>;
                const filled = variables[seg.value];
                return filled
                    ? <Chip key={i} label={filled} size="small" color="success" variant="outlined"
                        sx={{ mx: 0.3, fontWeight: 600, fontSize: "0.85em", height: 22, verticalAlign: "middle" }} />
                    : <Chip key={i} label={`[${seg.value}]`} size="small" color="warning" variant="filled"
                        sx={{ mx: 0.3, fontSize: "0.82em", height: 22, verticalAlign: "middle" }} />;
            })}
        </span>
    );
}

export default function SendEmailPage() {
    const memberStore = useStore((s) => s.memberStore);
    const emailTemplateStore = useStore((s) => s.emailTemplateStore);
    const emailLogStore = useStore((s) => s.emailLogStore);
    const modalStore = useStore((s) => s.modalStore);
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
    const [templateId, setTemplateId] = useState<string>("");
    const [customBody, setCustomBody] = useState("");
    const [useTemplate, setUseTemplate] = useState(true);
    const [templateVariables, setTemplateVariables] = useState<string[]>([]);
    const [variableValues, setVariableValues] = useState<Record<string, string>>({});
    const [sendingEmail, setSendingEmail] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({ open: false, message: "", severity: "success" });
    const { t } = useTranslation();
    const memberStatusOptions = useMemberStatusOptions();

    const selectedTemplate = useMemo(
        () => emailTemplateStore.templates.find((tpl) => tpl.id === templateId) ?? null,
        [templateId, emailTemplateStore.templates]
    );

    useEffect(() => {
        emailTemplateStore.fetchTemplates();
    }, [emailTemplateStore.fetchTemplates]);

    useEffect(() => {
        if (!useTemplate || !templateId) {
            setTemplateVariables([]);
            setVariableValues({});
            return;
        }
        const tpl = emailTemplateStore.templates.find((t) => t.id === templateId);
        if (!tpl) {
            setTemplateVariables([]);
            setVariableValues({});
            return;
        }
        const pattern = /\[([^\]]+)\]/g;
        const found = new Set<string>();
        let match;
        const combined = `${tpl.subject || ""} ${tpl.body || ""}`;
        while ((match = pattern.exec(combined)) !== null) {
            found.add(match[1]);
        }
        const vars = Array.from(found);
        setTemplateVariables(vars);
        setVariableValues((prev) => {
            const next: Record<string, string> = {};
            vars.forEach((v) => { next[v] = prev[v] ?? ""; });
            return next;
        });
    }, [templateId, useTemplate, emailTemplateStore.templates]);

    function getFilterProps(): FilterProps {
        const filters: any = {};
        return { filters, page: 1, pageSize: 10 };
    }

    const handleSend = async () => {
        setSendingEmail(true);
        try {
            await emailLogStore.sendEmail({
                memberIds: selectedMemberIds,
                templateId: useTemplate ? templateId : undefined,
                body: useTemplate ? undefined : customBody,
                variables: useTemplate && templateVariables.length > 0 ? variableValues : undefined,
            });
            setSnackbar({ open: true, message: t("emails.send.successSend"), severity: "success" });
        } catch {
            setSnackbar({ open: true, message: t("emails.send.errorSend"), severity: "error" });
        } finally {
            setSendingEmail(false);
        }
    };

    const handleSendConfirm = () => {
        modalStore.openModal(
            <ConfirmComponent
                text={t("general.areYouSure")}
                onYes={async () => {
                    modalStore.closeModal();
                    handleSend();
                }}
                onCancel={modalStore.closeModal}
            />
        );
    };

    const handleGenerateBatchPdf = async () => {
        setGeneratingPdf(true);
        try {
            await emailLogStore.generateBatchLettersPdf(
                selectedMemberIds,
                useTemplate ? templateId : undefined,
                useTemplate ? undefined : customBody,
                useTemplate && templateVariables.length > 0 ? variableValues : undefined
            );
            setSnackbar({ open: true, message: t("emails.send.successPdf"), severity: "success" });
        } catch {
            setSnackbar({ open: true, message: t("emails.send.errorPdf"), severity: "error" });
        } finally {
            setGeneratingPdf(false);
        }
    };

    const columns = [
        {
            key: "memberNumber", header: t("members.memberNumber"), dataType: DataType.NUMBER, filterable: true,
        },
        {
            key: "firstName", header: t("members.firstName"), filterable: true,
        },
        {
            key: "lastName", header: t("members.lastName"), filterable: true,
        },
        {
            key: "email", header: t("members.email"), filterable: true,
        },
        {
            key: "status",
            header: t("members.status"),
            options: memberStatusOptions,
            dataType: DataType.SELECT,
            filterable: true,
            width: '80px'
        },
    ];

    const handleRowSelect = (member: Member, checked: boolean) => {
        setSelectedMemberIds((prev) =>
            checked
                ? [...prev, member.id!]
                : prev.filter((id) => id !== member.id)
        );
    };

    const handleSelectAll = async () => {
        const currentOrderBy = memberStore.tableData.orderBy || "memberNumber";
        const currentSortDirection = memberStore.tableData.orderBy || "asc";
        const filterProps = { ...getFilterProps(), page: 1, pageSize: 10000, orderBy: currentOrderBy, sortDirection: currentSortDirection };
        const allMembersResult = await memberStore.loadMembers(filterProps);
        if (allMembersResult && allMembersResult.data) {
            setSelectedMemberIds(allMembersResult.data.map((m: Member) => m.id!));
            memberStore.loadMembers({ ...getFilterProps(), orderBy: currentOrderBy, sortDirection: currentSortDirection });
        } else {
            const allIds = Array.from(memberStore.tableData.list.values()).map((m) => m.id!);
            setSelectedMemberIds(allIds);
        }
    };

    const handleDeselectAll = () => {
        setSelectedMemberIds([]);
        const currentOrderBy = memberStore.tableData.orderBy || "memberNumber";
        const currentSortDirection = memberStore.tableData.orderBy || "asc";
        memberStore.loadMembers({ ...getFilterProps(), orderBy: currentOrderBy, sortDirection: currentSortDirection });
    };

    return (
        <Paper sx={{ p: 3, maxWidth: 900, mx: "auto", mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {t("emails.send.title")}
            </Typography>
            <Box sx={{ mb: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleSelectAll}
                    sx={{ mr: 2 }}
                >
                    {t("emails.send.selectAll")}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleDeselectAll}
                >
                    {t("emails.send.deselectAll")}
                </Button>
            </Box>
            <DataTable
                columns={[
                    {
                        key: "select",
                        header: "",
                        render: (item: Member) => (
                            <Checkbox
                                checked={selectedMemberIds.includes(item.id!)}
                                onChange={(event) => handleRowSelect(item, event.target.checked)}
                                color="primary"
                            />
                        ),
                        width: "50px",
                    },
                    ...columns,
                ]}
                data={memberStore.tableData}
                onFilter={(filterProps: FilterProps) => memberStore.loadMembers(filterProps)}
                disableRowClick={true}
                defaultOrderByColumn="memberNumber"
            />
            <Box sx={{ mt: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={useTemplate}
                            onChange={(_, checked) => setUseTemplate(!!checked)}
                            color="primary"
                        />
                    }
                    label={t("emails.send.useTemplate")}
                />
            </Box>
            {useTemplate ? (
                <>
                    <TextField
                        select
                        label={t("emails.send.template")}
                        value={templateId}
                        onChange={(e) => setTemplateId(e.target.value)}
                        fullWidth
                        variant="standard"
                        sx={{ mt: 2, mb: 2 }}
                    >
                        {emailTemplateStore.templates.map(tpl => (
                            <MenuItem key={tpl.id} value={tpl.id}>
                                {tpl.subject}
                            </MenuItem>
                        ))}
                    </TextField>
                    {templateVariables.length > 0 && (
                        <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                {t("emails.send.fillTemplateVariables")}
                            </Typography>
                            {templateVariables.map((varName) => (
                                <TextField
                                    key={varName}
                                    label={`[${varName}]`}
                                    value={variableValues[varName] ?? ""}
                                    onChange={(e) =>
                                        setVariableValues((prev) => ({ ...prev, [varName]: e.target.value }))
                                    }
                                    fullWidth
                                    variant="standard"
                                    sx={{ mb: 1.5 }}
                                />
                            ))}
                        </Box>
                    )}
                    {selectedTemplate && (
                        <Box sx={{ mt: 2, mb: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                            <Box sx={{ px: 2, py: 1, bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                    {t("emails.send.preview")}
                                </Typography>
                            </Box>
                            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                    {t("emails.templates.subject")}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, mt: 0.25 }}>
                                    <PreviewText text={selectedTemplate.subject || ""} variables={variableValues} />
                                </Typography>
                                <Divider />
                                <Box sx={{ py: 1.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                        {t("emails.templates.body")}
                                    </Typography>
                                    <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                                        <PreviewText text={selectedTemplate.body || ""} variables={variableValues} />
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </>
            ) : (
                <>
                    <TextField
                        label={t("emails.send.customBody")}
                        value={customBody}
                        onChange={e => setCustomBody(e.target.value)}
                        multiline
                        rows={6}
                        fullWidth
                        variant="standard"
                        sx={{ mt: 2, mb: 2 }}
                    />
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {t("emails.templates.variablesTitle")}
                        </Typography>
                        <Typography variant="body2">
                            {t("emails.templates.variablesDescription")}
                        </Typography>
                        <ul style={{ marginLeft: 16 }}>
                            <li>{t("emails.templates.variableMemberNumber")}</li>
                            <li>{t("emails.templates.variableFirstName")}</li>
                            <li>{t("emails.templates.variableLastName")}</li>
                        </ul>
                    </Alert>
                </>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleSendConfirm}
                    disabled={selectedMemberIds.length === 0 || sendingEmail || generatingPdf}
                    startIcon={sendingEmail ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {sendingEmail ? t("emails.send.sending") : t("emails.send.send")}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateBatchPdf}
                    disabled={selectedMemberIds.length === 0 || generatingPdf || sendingEmail}
                    startIcon={generatingPdf ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {generatingPdf ? t("emails.send.generatingPdf") : t("emails.send.generateBatchPdf")}
                </Button>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}

